import { Box, Divider, Stack, Typography } from '@mui/material'
import { Error } from 'components/Error'
import { Loading } from 'components/Loading'
import { useLotQuery } from 'services/lots'
import { ITender } from 'types/tender'
import { LotInformation } from './lots/lot-infromation'
import { useTranslation } from 'react-i18next'
import TenderEvaluationSubmissions from './tender-evaluation-submissions'
import { ESubmissionEvaluationStatus } from 'types/submissions/submissions-evaluation'
import { useSubmissionEvaluationsQuery } from 'services/submissions/submission-evaluation'
import { useQueryState } from 'react-router-use-location-state'
import { TablePagination } from 'components/TablePagination'

type Props = {
  tender: ITender
}

const TenderEvaluation = ({ tender }: Props) => {
  const { t } = useTranslation()

  const [page, setPage] = useQueryState('page', 0)
  const rowsPerPage = 10

  const args = {
    page,
    size: rowsPerPage,
    tenderId: tender.id,
  } as const

  const {
    data: submissionEvaluations,
    isLoading,
    isError,
  } = useSubmissionEvaluationsQuery(args, {
    skip: !tender.id,
    refetchOnMountOrArgChange: true,
  })

  const {
    data: lot,
    isLoading: isLotLoading,
    isError: isLotError,
  } = useLotQuery({
    id: tender.lot.id ?? '',
  })

  if (isLoading || isLotLoading) return <Loading fullPage={false} />
  if (isLotError && isError) return <Error />
  if (submissionEvaluations === undefined || lot === undefined) {
    return <></>
  }

  const generalSubmissioCount = submissionEvaluations.items.length ?? 0
  const evaluatedSubmissioCount =
    submissionEvaluations.items.filter(
      (subEva) => subEva.status === ESubmissionEvaluationStatus.FINISHED
    ).length ?? 0
  const rejectedSubmissioCount =
    submissionEvaluations.items.filter(
      (subEva) =>
        subEva.status === ESubmissionEvaluationStatus.DECLINED ||
        subEva.status === ESubmissionEvaluationStatus.REJECTED
    ).length ?? 0

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ backgroundColor: 'primary.main', p: 2 }}
      >
        <Typography variant="body2" color="white">
          {`${t('lot-name')}: ${lot.name}`}
        </Typography>
      </Stack>
      <Divider flexItem />
      <LotInformation lot={lot} />
      <Box
        sx={{
          display: 'flex',
          gap: '15px',
        }}
      >
        <Typography sx={{ fontWeight: 600 }}>{t('tender-proposal')}:</Typography>
        <Typography>
          {t('general-submission-count')}: {generalSubmissioCount}
        </Typography>
        <Typography>
          {t('evaluated-submission-count')}: {evaluatedSubmissioCount}
        </Typography>
        <Typography>
          {t('rejected-submission-count')}: {rejectedSubmissioCount}
        </Typography>
      </Box>
      <Divider flexItem />
      <Box sx={{ maxWidth: '100%' }}>
        {submissionEvaluations.items.length > 0 &&
          submissionEvaluations.items.map((subEva) => {
            return (
              <TenderEvaluationSubmissions
                key={subEva.submissionEvaluationId}
                subEvaListItem={subEva}
                lot={lot}
                tender={tender}
              />
            )
          })}
        {submissionEvaluations.info.totalRows !== 0 && (
          <TablePagination
            total={submissionEvaluations.info.totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={setPage}
          />
        )}
      </Box>
    </Stack>
  )
}

export default TenderEvaluation
